import { useEffect, useMemo, useRef, useState } from 'react';
import { loadScript, randomId } from '@mygpt/app/utils';

function generateRandomId() {
  // Generate a random positive 32-bit integer (from 1 to 2^31-1)
  return `${Math.floor(Math.random() * (2 ** 31 - 1)) + 1}`;
}

// 定义消息数组的类型
interface Message {
  isAnswer: boolean;
  text: string;
  translation?: string;
  timestamp: number;
  userId: string;
  end: boolean;
}

export const useVoiceRealtime = ({ botId, baseUrl }: { botId: string; baseUrl: string }) => {
  const [realtimeStatus, setRealtimeStatus] = useState<'thinking' | 'speaking' | 'listening' | 'idle'>('listening');
  const [realtimeMessages, setRealtimeMessages] = useState<Message[]>([]);

  const [language, setLanguage] = useState('ja');
  const [state, setState] = useState('leave');
  const [userId, setUserId] = useState('');
  const [taskId, setTaskId] = useState('');

  const [joinLoading, setJoinLoading] = useState(false);

  const trtcSdkAppidRef = useRef<number>(0);
  const userSigRef = useRef<string>('');
  const robotUserSigRef = useRef<string>('');

  const trtcClientRef = useRef(null);

  const robotUserId = useMemo(() => userId + '_robot', [userId]);

  const [isMicrophoneOn, setIsMicrophoneOn] = useState(true);

  const messagesRef = useRef<Message[]>([]);

  useEffect(() => {
    const scripts = ['https://web.sdk.qcloud.com/trtc/webrtc/v5/test/latest/dist/trtc.js'];

    for (const script of scripts) {
      loadScript(script);
    }

    setUserId(generateRandomId());
  }, []);

  const changeState = (s) => {
    setState(s);
    // 更新按钮状态的逻辑可以在组件中处理
  };

  async function RequestCloudApi({ action, payload }) {
    let res = await fetch(baseUrl + '/action', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Action: action,
      },
      body: payload,
    });
    return res;
  }

  async function startAIConversation(sessionId: string) {
    const data = {
      SdkAppId: trtcSdkAppidRef.current,
      RoomId: userId,
      SessionId: sessionId,
      AgentConfig: {
        UserId: robotUserId,
        UserSig: robotUserSigRef.current,
      },
      Language: language,
      BotId: botId,
    };
    //data["AgentConfig"]["AgentMode"] = 1;
    data['AgentConfig']['TargetUserId'] = userId;

    data['LLMConfig'] = '';
    data['TTSConfig'] = '';

    const options = {
      action: 'StartAIConversation',
      payload: JSON.stringify(data),
    };
    try {
      let res = await RequestCloudApi(options);
      let resJson = await res.json();
      console.log(resJson);
      setTaskId(resJson.TaskId);
    } catch (error) {
      console.error(error);
    }

    changeState('transcription');
  }

  async function stopAIConversation() {
    const data = {
      TaskId: taskId,
    };
    const options = {
      action: 'StopAIConversation',
      payload: JSON.stringify(data),
    };
    try {
      let res = await RequestCloudApi(options);
      let resJson = await res.json();
      console.log(resJson);
    } catch (error) {
      console.error(error);
    }
    changeState('join');
  }

  function handleMessage(data) {
    function updateMessages(newMessage: Message) {
      const prevMessages = messagesRef.current;
      let updatedMessages: Message[];

      // 如果没有消息，直接添加新消息
      if (prevMessages.length === 0) {
        updatedMessages = [newMessage];
      } else {
        const lastMessage = prevMessages[prevMessages.length - 1];

        // 如果上一条消息是提问且未结束，则更新上一条消息
        if (!lastMessage.end && !lastMessage.isAnswer) {
          updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            text: newMessage.text,
            translation: newMessage.translation || lastMessage.translation,
            end: newMessage.end,
          };
        } else {
          // 判断消息返回的文字是否为空，如果为空则不添加消息
          updatedMessages = !newMessage.text ? prevMessages : [...prevMessages, newMessage];
        }
      }

      messagesRef.current = updatedMessages; // 同步更新 ref
      setRealtimeMessages(updatedMessages); // 更新状态触发重渲染
    }

    const isAnswer = data.sender.includes('_robot');

    const isAnswerEnd = isAnswer && data.payload.end;

    if (data.type == 10000) {
      if (!isAnswerEnd) {
        const newMessage: Message = {
          isAnswer: isAnswer,
          text: data.payload.text,
          translation: data.payload.translation_text || '',
          timestamp: Date.now(),
          userId: data.sender,
          end: data.payload.end,
        };

        // 更新消息数组
        updateMessages(newMessage);
        setRealtimeStatus(isMicrophoneOn ? 'speaking' : 'idle');
      } else {
        setRealtimeStatus(isMicrophoneOn ? 'listening' : 'idle');
      }
    }
  }

  async function join() {
    setJoinLoading(true);

    const sessionId = randomId();

    const data = {
      userid: userId,
      msg_type: 'trtc',
    };
    const options = {
      action: 'join',
      payload: JSON.stringify(data),
    };

    try {
      let res = await RequestCloudApi(options);
      let resJson = await res.json();
      console.log(resJson);

      trtcSdkAppidRef.current = resJson.sdkappid;
      userSigRef.current = resJson.usersig;
      robotUserSigRef.current = resJson.robot_usersig;
    } catch (error) {
      console.error(error);
      setJoinLoading(false);
    }

    trtcClientRef.current = TRTC.create();

    trtcClientRef.current?.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, ({ userId, streamType }) => {
      // 为了播放视频画面，您需在 DOM 中放置一个 HTMLElement，可以是一个 div 标签，假设其 id 为 `${userId}_${streamType}`
      const view = 'remote_video';
      trtcClientRef.current?.startRemoteVideo({ userId, streamType, view });
    });

    trtcClientRef.current?.on(TRTC.EVENT.CUSTOM_MESSAGE, (event) => {
      let data = new TextDecoder().decode(event.data);
      let jsonData = JSON.parse(data);
      console.log(`receive custom msg from ${event.userId} cmdId: ${event.cmdId} seq: ${event.seq} data: ${data}`);
      handleMessage(jsonData);
    });

    try {
      await trtcClientRef.current?.enterRoom({
        roomId: parseInt(userId, 10),
        scene: 'rtc',
        sdkAppId: trtcSdkAppidRef.current,
        userId: userId,
        userSig: userSigRef.current,
      });
      console.log('进房成功');
    } catch (error) {
      console.error('进房失败 ' + error);
      setJoinLoading(false);
    }

    await trtcClientRef.current?.startLocalAudio();
    await trtcClientRef.current?.startPlugin('AIDenoiser', {
      assetsPath: 'https://cdn.jsdelivr.net/gh/mikeyang01/static_hosting@master',
      sdkAppId: trtcSdkAppidRef.current,
      userId: userId,
      userSig: userSigRef.current,
    });

    setIsMicrophoneOn(true);
    await startAIConversation(sessionId);
    changeState('join');
    setJoinLoading(false);
  }

  const toggleMicrophone = async () => {
    if (!trtcClientRef.current) return;

    try {
      if (isMicrophoneOn) {
        await trtcClientRef.current?.stopLocalAudio();
        setIsMicrophoneOn(false);
        setRealtimeStatus('idle');
      } else {
        await trtcClientRef.current?.startLocalAudio();
        setIsMicrophoneOn(true);
        realtimeStatus === 'idle' && setRealtimeStatus('listening');
      }
    } catch (error) {
      console.error('麦克风控制失败:', error);
    }
  };

  async function leave() {
    await stopAIConversation();
    await trtcClientRef.current?.exitRoom();
    await trtcClientRef.current?.stopPlugin('AIDenoiser');
    // 退房成功后，若后续无需使用 trtc 实例，则可以调用 trtc.destroy 方法销毁实例，及时释放相关资源。销毁后的 trtc 实例无法继续使用，需要重新创建新的实例。
    trtcClientRef.current?.destroy();
    setRealtimeMessages([]);
    changeState('leave');
    userSigRef.current = '';
    robotUserSigRef.current = '';
  }

  useEffect(() => {
    if (!language) return;
    if (state === 'join') {
      leave().then(() => {
        join();
      });
    }
  }, [language]);

  return {
    setLanguage,
    state,
    join,
    leave,
    joinLoading,
    realtimeMessages,
    isMicrophoneOn,
    toggleMicrophone,
    realtimeStatus,
  };
};
